/**
 * Baupläne je Post-Art.
 *
 * Ein Blueprint sagt, wie ein Beitrag dieser Art gebaut ist: welche Rolle
 * jede Folie hat, wie der Hook klingt, was das Bild je Rolle zeigt, wie die
 * Zeilen geschrieben sind, worauf die Caption hinausläuft und woran solche
 * Beiträge typischerweise scheitern. Der Planer wählt in EINEM Aufruf genau
 * einen Bauplan (das Schema erzwingt die Wahl) und füllt ihn mit der Idee.
 *
 * Warum so ausführlich: Vorher stand im Prompt „siehe FORMATE", der
 * Abschnitt war aber auf eine Zeile geschrumpft. Das Modell erfand die
 * Struktur jedes Mal neu, und genau so sahen die Beiträge aus: beliebig.
 *
 * Die Schlüssel sind die Erzählarten der Rezepte (STORY_FORMATS).
 */
export const BLUEPRINTS = [
    {
        key: 'GESCHICHTE', title: 'Geschichte',
        use: 'Emotion und Wiedererkennung. Eine Person erlebt ein konkretes Problem, eine belegte Funktion dreht die Situation. Stark für Downloads und Verkäufe.',
        slides: [4, 7],
        beats: [
            'HOOK: der genaue Moment, in dem das Problem spürbar wird, mit Person und Situation in einem Satz.',
            'KONFLIKT: was konkret schiefläuft, beobachtbar, kein Gefühlswort als Ersatz.',
            'ZUSPITZUNG: der Augenblick, in dem es kippt (ein Gedanke, eine Nachricht, ein Satz von jemandem).',
            'WENDUNG: die eine Handlung mit dem Produkt, an der belegten Funktion gezeigt (hier gehört die echte Vorlage hin).',
            'ERGEBNIS: was danach sichtbar anders ist.',
            'POINTE (optional): ein Satz, den man weiterschickt, kein Werbespruch und keine Moral.',
        ],
        hook: 'Konkret und szenisch, etwa „14 Leute im Gruppenchat. Gekommen ist keiner." Nie „Kennst du das?".',
        images: 'Dieselbe Figur mit demselben Beschreibungssatz, derselbe Ort und Stil. Einstellungen wechseln: Totale, Halbnah, Detail der Hände oder des Geräts, Blick über die Schulter auf den Bildschirm, Reaktion.',
        text: 'Gesprochene Sätze im Präsens, wie man es einer Freundin erzählt. Jede Folie ein Schritt der Handlung.',
        caption: 'Erzählt weiter, was die Folien nicht sagen konnten (wer, warum gerade jetzt), dann der nächste Schritt passend zum Ziel.',
        pitfalls: 'Die Figur guckt nur traurig. Das Produkt löst alles wie Magie. Moral am Ende. Ein Zeitsprung, den niemand versteht.',
    },
    {
        key: 'MINI-GESCHICHTE', title: 'Kurze Szene / POV',
        use: 'Reichweite durch Wiedererkennen. Ein Alltagsmoment, den die Zielgruppe sofort kennt, mit einer Wendung.',
        slides: [2, 4],
        beats: [
            'HOOK: „POV:" oder eine Situation in einem Satz, die jeder schon erlebt hat.',
            'DETAIL: das peinlich genaue Detail, an dem man sich ertappt fühlt.',
            'WENDUNG: was man diesmal anders macht, gern mit dem Produkt.',
            'POINTE (optional): trockener Abschluss.',
        ],
        hook: 'Ich- oder Du-Perspektive, sehr konkret: „POV: Du schreibst „wir müssen mal wieder" zum dritten Mal in diesem Jahr."',
        images: 'Ich-Perspektive: Hände, Gegenstände, Bildschirm, Blick aus den Augen der Person. Gesichter sind nicht nötig.',
        text: 'Kurz, trocken, beobachtend. Keine Erklärung, der Witz liegt im Detail.',
        caption: 'Ein Satz, der die Szene weiterspinnt, dann eine Frage, die Kommentare auslöst.',
        pitfalls: 'Eine Situation, die niemand so erlebt. Die Pointe wird erklärt.',
    },
    {
        key: 'LISTE', title: 'Liste',
        use: 'Speichern und Teilen. Mehrere konkrete, nützliche Punkte zu einem Anlass.',
        slides: [4, 8],
        beats: [
            'HOOK: Zahl plus konkreter Nutzen plus Kontext („5 Orte in Dortmund, an denen du ohne Date-Druck Leute triffst").',
            'PUNKT 1 bis n: je Folie ein Punkt, nummeriert („1/5"), Name des Punkts plus warum in einem Satz.',
            'BONUS ODER FAZIT (optional): der stärkste Punkt zum Schluss oder ein Geheimtipp.',
        ],
        hook: 'Genau die Anzahl, die folgt. Konkreter Kontext statt „Tipps für mehr …".',
        images: 'Jeder Punkt ein eigenes konkretes Motiv, keine Wiederholung. Bei rein informativen Punkten darf eine Info-Karte (style list) die Folie tragen.',
        text: 'Je Punkt: was es ist und der eine Grund, warum es hilft. Keine Allgemeinplätze.',
        caption: 'Welcher Punkt am meisten überrascht und warum, dann „Speicher dir das für …".',
        pitfalls: 'Anzahl im Hook stimmt nicht. Punkte, die auf jede Stadt oder jedes Produkt passen.',
    },
    {
        key: 'ANLEITUNG', title: 'Anleitung',
        use: 'Vertrauen und Downloads. Schritt für Schritt zu einem klaren Ergebnis, oft direkt im Produkt.',
        slides: [4, 7],
        beats: [
            'HOOK: das Ergebnis versprechen, mit Aufwand („So steht euer Treffen in zwei Minuten, ohne 40 Nachrichten").',
            'AUSGANGSLAGE (optional): ein Satz, warum es heute nervt.',
            'SCHRITT 1 bis n: Verb zuerst, ein Schritt je Folie, konkret. Im Produkt mit der passenden echten Vorlage.',
            'ERGEBNIS: wie es danach aussieht.',
        ],
        hook: 'Ergebnis plus Zeit oder Aufwand. Kein „Hier ist eine Anleitung".',
        images: 'Hände bei der Handlung, das Gerät mit dem echten Bildschirm, Details. Kurze Schrittfolgen dürfen als Karte (style steps) laufen.',
        text: 'Imperativ, kurz, eindeutig. Jeder Schritt lässt sich sofort nachmachen.',
        caption: 'Für wen das besonders hilft und der erste Schritt zum Ausprobieren.',
        pitfalls: 'Schritte, die das Produkt nicht kann. Zu viele Schritte auf einer Folie.',
    },
    {
        key: 'IRRTUM', title: 'Mythos aufklären',
        use: 'Kommentare und Autorität. Ein verbreiteter Glaubenssatz der Zielgruppe wird verständnisvoll widerlegt.',
        slides: [3, 5],
        beats: [
            'HOOK: der Mythos wörtlich, als hätte ihn jemand gesagt.',
            'WARUM MAN DAS GLAUBT: verständnisvoll, ohne die Zielgruppe dumm dastehen zu lassen.',
            'WAS STIMMT: mit Grund, nur belegte Aussagen.',
            'WAS DAS FÜR DICH HEISST: konkret für die eigene Situation.',
            'BRÜCKE (optional): wie das Produkt dabei hilft.',
        ],
        hook: 'Der Mythos in Anführungszeichen, dazu eine kurze Kennzeichnung („Stimmt nicht.").',
        images: 'Die Situation, in der der Mythos entsteht, dann die Situation, in der er sich auflöst. Mythos gegen Wahrheit darf als Karte (style myth) laufen.',
        text: 'Klar, ruhig, ohne Belehrung. Keine Scheingesetze und keine erfundenen Zahlen.',
        caption: 'Wo der Mythos herkommt und eine Frage an die Zuschauer, was sie geglaubt haben.',
        pitfalls: '„Das Problem ist nicht …"-Floskeln. Eine Widerlegung ohne Beleg.',
    },
    {
        key: 'THESE', title: 'Meinung',
        use: 'Diskussion und Reichweite. Eine pointierte, aber vertretbare Haltung.',
        slides: [3, 5],
        beats: [
            'HOOK: die These, steil, aber haltbar.',
            'GRUND 1: der stärkste Grund, konkret.',
            'GRUND 2 ODER EINWAND: den naheliegenden Einwand nennen und entkräften.',
            'KONSEQUENZ: was man ab morgen anders macht, plus Frage an die Zuschauer.',
        ],
        hook: 'Eine klare Aussage ohne Absicherung, etwa „Gruppenchats sind der Grund, warum ihr euch nie seht."',
        images: 'Redaktionell und konkret: die Situation, über die gestritten wird, nicht symbolische Stockfotos.',
        text: 'Selbstbewusst, aber fair. Keine Beleidigung, kein Clickbait.',
        caption: 'Die Haltung in einem Satz und die ausdrückliche Einladung zum Widerspruch.',
        pitfalls: 'Eine These, die niemand bestreitet. Eine These, die das Produkt nicht stützt.',
    },
    {
        key: 'TYPEN', title: 'Typen',
        use: 'Markieren und Teilen. Liebevolle Charaktertypen einer Situation, in denen man Freunde wiedererkennt.',
        slides: [4, 7],
        beats: [
            'HOOK: „Die 5 Typen bei …" mit einer konkreten Situation.',
            'TYP 1 bis n: Name des Typs plus sein typisches Verhalten in einem Satz.',
            'ABSCHLUSS: „Welcher bist du?" oder der Typ, der das Produkt nutzt.',
        ],
        hook: 'Zahl plus Situation, die jeder aus der Zielgruppe kennt.',
        images: 'Dieselbe Szene, je Typ eine andere Person in typischer Pose. Einheitlicher Stil, damit es eine Reihe ist.',
        text: 'Pointiert, liebevoll, nie verletzend.',
        caption: '„Markier den, der …" plus ein Satz zum Produkt, wenn er passt.',
        pitfalls: 'Typen, die beleidigen oder austauschbar sind.',
    },
    {
        key: 'VORLAGE', title: 'Satz zum Nachmachen',
        use: 'Speichern und direkte Nützlichkeit. Die genauen Worte für eine Situation, in der man sonst nicht weiß, was man sagen soll.',
        slides: [2, 4],
        beats: [
            'HOOK: die Situation, in der einem die Worte fehlen.',
            'DIE WORTE: wörtlich, als Nachricht oder Zitat (style chat oder quote).',
            'WARUM ES FUNKTIONIERT: ein Satz.',
            'VARIANTE (optional): für einen anderen Ton.',
        ],
        hook: '„Was du schreibst, wenn …" mit einer sehr konkreten Lage.',
        images: 'Das Gerät in der Hand mit der Nachricht als Karte darüber, oder die Szene, in der die Nachricht ankommt.',
        text: 'Die Vorlage selbst ist kurz und sofort kopierbar.',
        caption: 'Wann man sie verwendet, plus „Speicher dir das".',
        pitfalls: 'Worte, die niemand so sagen würde.',
    },
    {
        key: 'GUIDE', title: 'Tipps & Orte',
        use: 'Lokale Relevanz und Speichern. Optionen für einen Anlass, mit einem Geheimtipp.',
        slides: [4, 7],
        beats: [
            'HOOK: der Anlass („Regen-Sonntag in Dortmund und keine Lust auf Couch").',
            'OPTION 1 bis n: je Folie eine Option: was, für wen, ein konkretes Detail.',
            'GEHEIMTIPP: die überraschende Option.',
            'SO KLAPPT ES (optional): wie man es mit dem Produkt organisiert.',
        ],
        hook: 'Anlass plus Ort plus Stimmung.',
        images: 'Jede Option als eigener, konkreter Ort oder eigene Tätigkeit. Echte Orte nur, wenn sie im Briefing oder in der Recherche stehen, sonst Kategorien statt Namen.',
        text: 'Anschaulich, mit einem Detail, das man sich merkt.',
        caption: 'Welche Option man zuerst probieren sollte und wen man mitnimmt.',
        pitfalls: 'Erfundene Orte oder Öffnungszeiten.',
    },
    {
        key: 'VERGLEICH', title: 'Vorher / Nachher',
        use: 'Klarer Nutzen auf einen Blick. Dieselbe Situation ohne und mit dem Produkt.',
        slides: [3, 5],
        beats: [
            'HOOK: der Kontrast in einem Satz.',
            'VORHER: die Situation konkret, wie sie heute läuft.',
            'NACHHER: dieselbe Situation mit dem Produkt, gleicher Bildaufbau.',
            'DER UNTERSCHIED: in einem Satz, nur belegte Vorteile.',
        ],
        hook: '„Vorher: … Jetzt: …" oder eine Frage, die den Unterschied anteasert.',
        images: 'Vorher und Nachher mit gleichem Ort und gleicher Einstellung, damit der Unterschied sofort sichtbar ist. Gegenüberstellungen dürfen als Karte (style compare) laufen.',
        text: 'Knapp, parallel gebaut.',
        caption: 'Für wen der Unterschied am größten ist und der nächste Schritt.',
        pitfalls: 'Ein Nachher, das das Produkt nicht belegen kann.',
    },
    {
        key: 'DEMO', title: 'Produkt in Aktion',
        use: 'Direkte Conversion. Eine belegte Funktion in einer echten Nutzungssituation.',
        slides: [3, 5],
        beats: [
            'HOOK: das Problem in einem Satz oder das überraschende Ergebnis.',
            'DIE FUNKTION: in echter Nutzung, mit der passenden echten Vorlage.',
            'WAS SIE LÖST: konkret und beobachtbar.',
            'NÄCHSTER SCHRITT: passend zum Ziel.',
        ],
        hook: 'Ergebnis oder Problem, nie der Produktname als erstes Wort.',
        images: 'Das Produkt oder Gerät in der Hand, in der echten Umgebung, natürlich benutzt.',
        text: 'Klar und konkret, eine Funktion pro Beitrag.',
        caption: 'Die Funktion in einem Satz und der Weg zum Produkt.',
        pitfalls: 'Mehrere Funktionen auf einmal. Werbesprache.',
    },
];
export const BLUEPRINT_BY_KEY = Object.fromEntries(BLUEPRINTS.map((b) => [b.key, b]));
/** Wie man auf eine Idee kommt, die nicht jeder hat. Gilt für alle Baupläne. */
export const IDEA_FORGE = [
    'IDEENSCHMIEDE: Entwickle intern drei Ansätze mit verschiedenen Techniken und nimm den überraschendsten, der wahr ist und zum Ziel passt:',
    '- Der peinlich genaue Moment: nicht das große Problem, sondern der Augenblick, in dem man es merkt.',
    '- Perspektivwechsel: aus Sicht eines Freundes, des Gastgebers, des Kellners, des Handys selbst.',
    '- Umkehrung: was passiert, wenn man es genau falsch macht.',
    '- Fakt als Überraschung: eine belegte Zahl oder Eigenschaft aus dem Briefing, die niemand erwartet.',
    '- Erwartung gegen Wirklichkeit: wie man es sich vorstellt und wie es läuft.',
    'Die Idee muss so konkret sein, dass sie nur zu diesem Produkt und dieser Zielgruppe passt.',
].join('\n');
/**
 * Die Baupläne als Prompttext. `keys` = vom Rezept erlaubte Erzählarten,
 * leer heißt alle. Bei einer Bildserie mit festem Folienbereich wird der
 * Bereich jedes Bauplans darauf zugeschnitten.
 */
export function blueprintBlock(keys, range) {
    const list = keys?.length ? BLUEPRINTS.filter((b) => keys.includes(b.key)) : BLUEPRINTS;
    const use = list.length ? list : BLUEPRINTS;
    const clamp = (b) => {
        if (!range)
            return b.slides;
        const lo = Math.max(range[0], Math.min(b.slides[0], range[1]));
        const hi = Math.max(lo, Math.min(range[1], b.slides[1]));
        return [lo, hi];
    };
    return [
        'BAUPLÄNE. Wähle genau EINEN, der Idee und Ziel am besten trägt, trag seinen Schlüssel ins Feld format ein und halte seine Folienrollen in dieser Reihenfolge ein:',
        ...use.map((b) => {
            const [lo, hi] = clamp(b);
            return [
                '',
                `## ${b.key} (${b.title}), ${lo === hi ? `${lo} Folien` : `${lo} bis ${hi} Folien`}`,
                `Wofür: ${b.use}`,
                'Folien:',
                ...b.beats.map((beat, i) => `  ${i + 1}. ${beat}`),
                `Hook: ${b.hook}`,
                `Bilder: ${b.images}`,
                `Text: ${b.text}`,
                `Caption: ${b.caption}`,
                `Scheitert an: ${b.pitfalls}`,
            ].join('\n');
        }),
        '',
        IDEA_FORGE,
    ].join('\n');
}
/** Folienbereich des gewählten Bauplans, für die Prüfung nach dem Planen. */
export function blueprintSlides(key) {
    return key ? BLUEPRINT_BY_KEY[key.trim().toUpperCase()]?.slides ?? null : null;
}
//# sourceMappingURL=blueprints.js.map